import { useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { supabase } from "../lib/supabase";
import bouttimeLogo from "../assets/bouttime-logo.svg";

export default function PasswordResetScreen({ appTheme, onComplete }) {
  const [password, setPassword]       = useState("");
  const [confirm, setConfirm]         = useState("");
  const [showPw, setShowPw]           = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    // Remove the recovery token from the URL so a page refresh doesn't re-trigger the gate
    window.history.replaceState(null, "", window.location.pathname);
    onComplete();
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px 16px",
    paddingRight: "46px",
    borderRadius: "14px",
    border: `1px solid ${appTheme.tealMid}`,
    background: appTheme.inputBackground,
    fontSize: "16px",
    color: appTheme.primaryText,
    outline: "none",
    fontFamily: appTheme.fontBody,
  };

  const eyeButtonStyle = {
    position: "absolute",
    right: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    touchAction: "manipulation",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: appTheme.pageBackground,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "52px 24px 48px",
    }}>
      <img src={bouttimeLogo} alt="BoutTime" style={{ height: "36px", width: "auto", marginBottom: "40px" }} />

      <div style={{ width: "100%", maxWidth: "400px" }}>
        <h1 style={{
          fontFamily: appTheme.fontHeading,
          fontSize: "30px",
          fontWeight: "600",
          color: appTheme.primaryText,
          margin: "0 0 10px",
          lineHeight: 1.2,
        }}>
          Set a new password
        </h1>
        <p style={{
          margin: "0 0 28px",
          fontFamily: appTheme.fontBody,
          fontSize: "15px",
          color: appTheme.mutedText,
          lineHeight: 1.6,
        }}>
          Choose a strong password for your account.
        </p>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

          {/* New password */}
          <div style={{ position: "relative" }}>
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              placeholder="New password (min 8 characters)"
              required
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowPw(p => !p)} style={eyeButtonStyle}>
              {showPw
                ? <EyeSlash size={18} color={appTheme.mutedText} />
                : <Eye size={18} color={appTheme.mutedText} />}
            </button>
          </div>
          {tooShort && (
            <p style={{ margin: 0, fontFamily: appTheme.fontBody, fontSize: "13px", color: appTheme.actionColor }}>
              Password must be at least 8 characters.
            </p>
          )}

          {/* Confirm password */}
          <div style={{ position: "relative" }}>
            <input
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(""); }}
              placeholder="Confirm new password"
              required
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowConfirm(p => !p)} style={eyeButtonStyle}>
              {showConfirm
                ? <EyeSlash size={18} color={appTheme.mutedText} />
                : <Eye size={18} color={appTheme.mutedText} />}
            </button>
          </div>
          {mismatch && (
            <p style={{ margin: 0, fontFamily: appTheme.fontBody, fontSize: "13px", color: appTheme.actionColor }}>
              Passwords don't match.
            </p>
          )}

          {error && (
            <p style={{ margin: 0, fontFamily: appTheme.fontBody, fontSize: "13px", color: appTheme.actionColor, textAlign: "center" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit || loading}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "14px",
              border: "none",
              background: appTheme.accentColor,
              color: "#fff",
              fontFamily: appTheme.fontBody,
              fontWeight: "700",
              fontSize: "16px",
              cursor: (!canSubmit || loading) ? "not-allowed" : "pointer",
              opacity: (!canSubmit || loading) ? 0.5 : 1,
              touchAction: "manipulation",
              marginTop: "4px",
            }}
          >
            {loading ? "Updating…" : "Update password"}
          </button>

        </form>
      </div>
    </div>
  );
}
